"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";


const navLinks = [
  { label: "Shop", href: "/shop" },
  { label: "Lab Testing", href: "/lab-testing" },
  { label: "About", href: "/about" },
  { label: "FAQ", href: "/faq" },
  { label: "Shipping & Returns", href: "/shipping-returns" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      {/* Announcement Bar */}
      <div className="announcement-bar">
        <span>Free Sample + 30-Day Money-Back Guarantee on Every Order</span>
      </div>
      {/* Header */}
      <header
        className={`sticky top-0 z-40 bg-warm-white transition-all duration-300 ${scrolled ? "header-scrolled" : "border-b border-border"}`}
        style={{ backgroundColor: scrolled ? "rgba(246,245,242,0.95)" : "#F6F5F2" }}
      >
        <div className="max-w-container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/homepage" className="flex items-center gap-3 group">
              <div className="h-14 flex items-center justify-center">
                <img
                  src="/assets/images/Untitled-1772518473290.jpeg"
                  alt="Kratapillar logo"
                  className="h-14 w-auto object-contain"
                />
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks?.map((link) => (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    pathname === link?.href
                      ? "text-olive" :"text-muted hover:text-charcoal"
                  }`}
                  style={{ fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <Link
                href="/cart"
                className={`text-sm font-medium transition-colors duration-200 ${
                  pathname === "/cart" ? "text-olive" : "text-muted hover:text-charcoal"
                }`}
                style={{ fontFamily: "Inter, system-ui, sans-serif" }}
                aria-label="Cart"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 6h18M16 10a4 4 0 01-8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link href="/shop" className="btn-primary text-xs py-2.5 px-5">
                Shop Now
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-1"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span
                className={`block h-0.5 w-6 bg-charcoal transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`}
              />
              <span
                className={`block h-0.5 w-6 bg-charcoal transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`}
              />
              <span
                className={`block h-0.5 w-6 bg-charcoal transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}
              />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-50 bg-warm-white transition-all duration-500 ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        style={{ backgroundColor: "#F6F5F2", top: "2.25rem" }}
      >
        <div className="flex flex-col h-full px-6 pt-20 pb-12">
          <nav className="flex flex-col gap-6 flex-1">
            {navLinks?.map((link, i) => (
              <Link
                key={link?.href}
                href={link?.href}
                className={`font-serif text-3xl font-semibold transition-colors ${
                  pathname === link?.href ? "text-olive" : "text-charcoal"
                }`}
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  transitionDelay: `${i * 60}ms`,
                  transform: menuOpen ? "translateX(0)" : "translateX(-20px)",
                  opacity: menuOpen ? 1 : 0,
                  transition: `all 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${i * 60}ms`,
                }}
              >
                {link?.label}
              </Link>
            ))}
          </nav>
          <Link
            href="/shop"
            className="btn-primary w-full justify-center text-sm"
          >
            Shop 1 Kilo
          </Link>
        </div>
        {/* Close button */}
        <button
          className="absolute top-4 right-6 p-2"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 6L18 18M18 6L6 18" stroke="#1F1F1F" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </>
  );
}